import React from "react";

export const palette = {
  light: {
    page: "#F5F4F1",
    pageBackground: "linear-gradient(to right, #FFF0E4, #E4E7FF)",
    card: "#FFFFFF",
    cardSoft: "#EEEDE9",
    line: "#E0DED8",
    lineStrong: "#CCCBC5",
    ink: "#1A1A18",
    muted: "#5A5A55",
    faint: "#8A8A82",
  },
  dark: {
    page: "#111315",
    pageBackground: "#111315",
    card: "#1B1E22",
    cardSoft: "#262A30",
    line: "#343941",
    lineStrong: "#4B5563",
    ink: "#F7F3EA",
    muted: "#C9C2B5",
    faint: "#8F98A3",
  },
};

export const themeTokens = (theme) => ({
  ...(theme === "dark" ? palette.dark : palette.light),
  brand: "#FD6600",
  primary: "#185FA5",
  primaryLight: theme === "dark" ? "#15304B" : "#E6F1FB",
  primaryMid: "#B5D4F4",
  success: "#3B6D11",
  successLight: theme === "dark" ? "#1F3218" : "#EAF3DE",
  warn: "#854F0B",
  warnLight: theme === "dark" ? "#392811" : "#FAEEDA",
  danger: "#A32D2D",
  dangerLight: theme === "dark" ? "#3A1D1D" : "#FCEBEB",
  accent: "#3C3489",
  accentLight: theme === "dark" ? "#29264B" : "#EEEDFE",
});

export function SectionLabel({ children, tokens }) {
  return (
    <div className="flex items-center gap-2 mt-6 mb-3">
      <span
        className="text-[12px] font-bold uppercase tracking-[0.2em] whitespace-nowrap"
        style={{ color: tokens.muted }}
      >
        {children}
      </span>
      <div className="flex-1 h-px" style={{ backgroundColor: tokens.lineStrong || tokens.line }} />
    </div>
  );
}

export function Badge({ children, variant = "blue", tokens }) {
  const map = {
    blue: { background: tokens.primaryLight, color: tokens.primary },
    green: { background: tokens.successLight, color: tokens.success },
    amber: { background: tokens.warnLight, color: tokens.warn },
    red: { background: tokens.dangerLight, color: tokens.danger },
    orange: { background: "#FFF0E6", color: tokens.brand },
    purple: { background: tokens.accentLight, color: tokens.accent },
    gray: { background: tokens.cardSoft, color: tokens.muted },
  };

  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full"
      style={map[variant]}
    >
      {children}
    </span>
  );
}

export function Card({ children, tokens, className = "" }) {
  return (
    <div
      className={`rounded-xl p-5 ${className}`}
      style={{
        backgroundColor: tokens.card,
        border: `1px solid ${tokens.line}`,
        boxShadow: "0 1px 3px rgba(0,0,0,.06),0 1px 2px rgba(0,0,0,.04)",
      }}
    >
      {children}
    </div>
  );
}

export function IconGlyph({ name, className = "", color = "currentColor" }) {
  const common = {
    className,
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  const paths = {
    user: <path d="M20 21a8 8 0 0 0-16 0M12 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z" />,
    mail: <path d="m4 6 8 7 8-7M4 6h16v12H4z" />,
    phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.4 2.1L8 9.7a16 16 0 0 0 6.3 6.3l1.3-1.3a2 2 0 0 1 2.1-.4c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2Z" />,
    building: <path d="M3 21h18M6 21V3h12v18M9 7h1M14 7h1M9 11h1M14 11h1M9 15h1M14 15h1" />,
    calendar: <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14H3V6a2 2 0 0 1 2-2Z" />,
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />,
    trend: <path d="m3 17 6-6 4 4 8-8M14 7h7v7" />,
    clock: <path d="M12 8v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />,
    users: <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />,
    file: <path d="M14 2H6a2 2 0 0 0-2 2v16h16V8zM14 2v6h6M8 13h8M8 17h6" />,
    alert: <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01" />,
    award: <path d="M8.2 13.1 7 22l5-3 5 3-1.2-8.9M17 8A5 5 0 1 1 7 8a5 5 0 0 1 10 0Z" />,
    flame: <path d="M8.5 14.5A4.5 4.5 0 0 0 13 21a6 6 0 0 0 6-6c0-4-3-6-4-9-1 3-3 4-5 6-.5-2 .5-4 2-6-4 2-7 5-7 9a6 6 0 0 0 2 4.5" />,
    pencil: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />,
    print: <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5h20v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z" />,
    refresh: <path d="M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5" />,
    moon: <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z" />,
    sun: <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4M17 12a5 5 0 1 1-10 0 5 5 0 0 1 10 0Z" />,
  };

  return <svg {...common}>{paths[name] || paths.user}</svg>;
}

export function InfoRow({ icon, label, value, badge, badgeVariant = "gray", tokens }) {
  return (
    <div
      className="flex items-center gap-2.5 py-2.5 last:border-0"
      style={{ borderBottom: `1px solid ${tokens.line}` }}
    >
      <div
        className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: tokens.cardSoft }}
      >
        <IconGlyph name={icon} className="w-3.5 h-3.5" color={tokens.faint} />
      </div>
      <span className="text-[12px] min-w-[120px] shrink-0" style={{ color: tokens.faint }}>
        {label}
      </span>
      <span className="text-[13px] font-medium flex-1 break-words" style={{ color: tokens.ink }}>
        {value || "N/A"}
      </span>
      {badge && <Badge variant={badgeVariant} tokens={tokens}>{badge}</Badge>}
    </div>
  );
}

